import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { ReportTableV3Component } from '../report-table-v3/report-table-v3.component';
import { TableRow } from '../report-table/report-table.component';
import { EllipsisCellComponent } from '../ellipsis-cell/ellipsis-cell.component';
import { ReportTableV4ActionsCellComponent } from './report-table-v4-actions-cell.component';

@Component({
  selector: 'ngx-report-table-v4',
  templateUrl: './report-table-v4.component.html',
  standalone: true,
  imports: [CommonModule, AgGridModule, ReportTableV4ActionsCellComponent],
  styleUrls: ['./report-table-v4.component.scss'],
})
export class ReportTableV4Component extends ReportTableV3Component {

  /** Column keys that are allowed to be sorted. All others are not sortable. */
  @Input() sortableColumns: string[] = ['firstName', 'lastName'];

  /**
   * Column keys that are always stripped from the displayed grid.
   * These columns stay in the underlying `tableData` (so they remain in
   * Excel exports driven by `docFromTable(tableData, ...)`), but are never
   * rendered in the grid. Defaults cover the two facility-column naming
   * conventions used across patient-assessments reports.
   */
  @Input() alwaysHiddenInUi: string[] = ['facility', 'facilityName'];

  /** Optional callbacks forwarded to the Advanced Directive actions cell. */
  @Input() onViewFile?: (fileName: string) => void;
  @Input() onDownloadFile?: (fileName: string) => void;
  @Input() onOpenOptionsMenu?: (event: { row: any; action: string | null }) => void;

  /**
   * No sort by default (per-column sortable via `sortableColumns`). Resize on.
   * Drag-locking is enforced per-column via `lockPosition` in `updateTableData`.
   * No native tooltip: `EllipsisCellComponent` owns the tooltip for every cell.
   */
  override defaultColDef: ColDef = {
    resizable: true,
    sortable: false,
    minWidth: 70,
    tooltipValueGetter: () => undefined as any,
  };

  /** Exposes the actions cell to AG Grid via `cellRenderer: 'actionsCell'`. */
  components = {
    ellipsisCell: EllipsisCellComponent,
    actionsCell: ReportTableV4ActionsCellComponent,
  };

  /**
   * Rebuilds `rows` and `columns` from `tableData` applying v4 rules:
   * - filter `hidden: true` columns and `alwaysHiddenInUi` keys from UI
   * - sortable only for keys in `sortableColumns`
   * - route `actions` column to the actions cell (locked right)
   * - mark the first non-pinned column with a visible left separator
   */
  protected override updateTableData(): void {
    if (!this.tableData) {
      return;
    }

    this.rows = this.tableData.rows;

    const tableHeaders = this.tableData.headers;
    const columnSeparators = (this.tableData as any).columnSeparators === true;
    const orderedAll = getOrderedHeaders(tableHeaders);

    const displayHeaders = orderedAll.filter(key => {
      if (!key) return false;
      const cfg: any = tableHeaders[key as keyof typeof tableHeaders];
      if (!cfg) return false;
      if (cfg.hidden === true) return false;
      if (this.alwaysHiddenInUi.includes(key)) return false;
      return true;
    });

    // Dynamic pinning — whichever column is flagged `boldLeftBorder: true`
    // defines both the pinned/scrolling boundary AND the visible section
    // divider, so the two stay aligned. Each report owns this flag (the
    // server sets it for custom reports; the parent decorates it for
    // AD / Anticoagulant / Diuretics). Falls back to the `pinnedLeftNumOfColumns`
    // input only when nothing in the data carries the flag.
    const numberOfColumnsAtLeftSide = displayHeaders.findIndex(key => {
      const cfg: any = tableHeaders[key as keyof typeof tableHeaders];
      return cfg?.boldLeftBorder === true;
    });
    const pinnedCount = numberOfColumnsAtLeftSide >= 0
      ? numberOfColumnsAtLeftSide
      : (this.pinnedLeftNumOfColumns || 0);

    // `columnSeparators` flag is intentionally ignored — v4 renders the
    // inter-column thin line on every non-first column for visual parity
    // across all patient-assessment reports (see the user's "why some
    // reports have lines and others don't" feedback).
    void columnSeparators;

    const flatCols: ColDef[] = displayHeaders.map((header, index) => {
      const headerConfig: any = tableHeaders[header as keyof typeof tableHeaders];
      const specifiedWidth = headerConfig['width'];
      const isActions = header === 'actions';
      const isPinned = pinnedCount > 0 && index < pinnedCount;
      const isDividerColumn = pinnedCount > 0 && index === pinnedCount;

      const borderClasses: string[] = [];
      if (isDividerColumn) {
        borderClasses.push('report-col-border-bold');
      } else if (index !== 0) {
        borderClasses.push('report-col-border');
      }
      const borderClass = borderClasses.join(' ');

      if (headerConfig['color']) {
        const style = document.createElement('style');
        const key = `list-ad-grid-col-${header}`;
        style.textContent = `.${key} { color: ${headerConfig['color']}; }`;
        document.body.appendChild(style);
      }

      const col: ColDef = {
        field: header,
        headerName: headerConfig['label'] || '',
        headerTooltip: headerConfig['label'] || undefined,
        sortable: this.sortableColumns.includes(header),
        // Click-sort ignores case; a cell is a string or a { value } object.
        comparator: (a: any, b: any) =>
          String(a?.value ?? a ?? '').localeCompare(String(b?.value ?? b ?? ''), 'en', { sensitivity: 'base' }),
        // Lock every column in place so a tiny viewport can't auto-unpin or
        // reorder them (see "columns reordered after resizing to 100px" bug).
        lockPosition: isPinned ? 'left' : (isActions ? 'right' : true),
        lockPinned: isPinned || isActions,
        valueGetter: isActions
          ? undefined
          : (p: any) => {
              const cell = p.data?.[header as keyof typeof p.data];
              if (typeof cell === 'string') return cell;
              return cell;
            },
        cellStyle: (p: any) => {
          if (isActions) {
            return { display: 'flex', alignItems: 'center', justifyContent: 'center' };
          }
          const colSettings: TableRow = headerConfig;
          const cell = p.data?.[header as keyof typeof p.data];
          const obj: any = {};
          if ((colSettings as any)?.bgColor) obj.backgroundColor = (colSettings as any).bgColor;
          if ((colSettings as any)?.color) obj.color = (colSettings as any).color;
          if ((colSettings as any)?.textAlign) obj.textAlign = (colSettings as any).textAlign;
          if ((colSettings as any)?.cursor) obj.cursor = (colSettings as any).cursor;
          if (cell && typeof cell === 'object') {
            if (cell.bgColor) obj.backgroundColor = cell.bgColor;
            if (cell.color) obj.color = cell.color;
            if (cell.textAlign) obj.textAlign = cell.textAlign;
            if (cell.cursor) obj.cursor = cell.cursor;
          }
          return Object.keys(obj).length > 0 ? obj : undefined;
        },
        headerClass: [
          headerConfig?.['color'] ? `list-ad-grid-col-${header}` : '',
          borderClass,
        ].filter(Boolean).join(' ') || undefined,
        cellClass: borderClass || undefined,
        cellRenderer: isActions ? 'actionsCell' : EllipsisCellComponent,
        cellRendererParams: isActions ? undefined : { header: headerConfig },
        pinned: isPinned ? 'left' : null,
        width: specifiedWidth || undefined,
        minWidth: isActions ? 170 : undefined,
      };

      if (isActions) {
        col.flex = 0;
        col.width = specifiedWidth ?? 170;
        col.minWidth = 170;
      }

      return col;
    });

    // Wrap contiguous columns that declare the same `group` field into an
    // AG-Grid column group so the 2-row "group name over its children" header
    // renders. Headers with no `group` stay top-level. Backward-compatible:
    // when no header has `group`, the output equals `flatCols`.
    const grouped: (ColDef | ColGroupDef)[] = [];
    let currentGroup: ColGroupDef | null = null;
    for (let i = 0; i < flatCols.length; i++) {
      const header = displayHeaders[i];
      const cfg: any = tableHeaders[header as keyof typeof tableHeaders];
      const groupName: string | undefined = cfg?.group;
      if (groupName) {
        if (currentGroup && currentGroup.headerName === groupName) {
          (currentGroup.children as ColDef[]).push(flatCols[i]);
        } else {
          currentGroup = { headerName: groupName, children: [flatCols[i]] };
          grouped.push(currentGroup);
        }
      } else {
        currentGroup = null;
        grouped.push(flatCols[i]);
      }
    }
    this.columns = grouped as ColDef[];
  }

  /**
   * Refit on viewport resize so the grid stays responsive while the user
   * resizes the window. AG Grid fires `gridSizeChanged` whenever the grid's
   * container changes size.
   *
   * Guarded: we only refit when the viewport is actually wide enough to
   * accommodate every column at its minimum width. At narrower widths AG Grid
   * can't honour `sizeColumnsToFit` without violating minWidths, and calling
   * it anyway leaves columns in a degenerate state that sometimes shuffles
   * their display order when the user resizes back to normal width.
   */
  onGridSizeChanged(_: any): void {
    if (!this.gridApi) return;
    if (!this.canFitColumns()) return;
    this.gridApi.sizeColumnsToFit();
  }

  /**
   * True when the viewport is wide enough for `sizeColumnsToFit` to produce
   * a sane layout — i.e. strictly wider than the sum of every column's
   * minimum width. Below that threshold we leave the grid alone and let
   * AG Grid fall back to horizontal scroll.
   */
  private canFitColumns(): boolean {
    if (!this.gridApi) return false;
    const cols = this.gridApi.getAllDisplayedColumns?.();
    if (!cols || !cols.length) return false;
    const totalMin = cols.reduce((acc, c: any) => acc + (c.getMinWidth?.() ?? 0), 0);
    const viewportEl = this.el?.nativeElement?.querySelector?.('.ag-body-viewport') as HTMLElement | null;
    const viewportWidth = viewportEl?.clientWidth ?? 0;
    return viewportWidth > totalMin;
  }

  /**
   * Responsive sizing — every v4 grid shrinks *and* expands its columns to
   * fit the available viewport, so all reports behave consistently when the
   * window is narrow. Steps:
   *   1. Auto-size any column explicitly flagged `autoSize: true` (content-fit).
   *   2. Run `sizeColumnsToFit` so non-auto-sized columns share the remaining
   *      viewport width proportionally.
   *
   * `sizeColumnsToFit` respects each column's `minWidth` (70 from defaultColDef,
   * 170 for the actions column). When the combined mins exceed the viewport,
   * AG Grid falls back to horizontal scroll — it never hides columns.
   */
  protected override applyAutoSize(): void {
    if (!this.gridApi || !this.tableData?.headers) return;
    const autoSizeKeys = Object.keys(this.tableData.headers)
      .filter(k => (this.tableData.headers as any)[k]['autoSize'] === true);
    setTimeout(() => {
      if (!this.gridApi) return;
      if (autoSizeKeys.length) {
        this.gridApi.autoSizeColumns(autoSizeKeys, false);
      }
      // Same guard as onGridSizeChanged — don't squash columns below their
      // minWidths when the viewport is tiny; AG Grid falls back to horizontal
      // scroll which is what we want.
      if (this.canFitColumns()) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 300);
  }
}

function getOrderedHeaders(headers: Record<string, { label: string, index?: number }>): string[] {
  const entries = Object.keys(headers);
  const withIndex = entries.filter(k => headers[k].index !== undefined);
  const withoutIndex = entries.filter(k => headers[k].index === undefined);
  const result: string[] = [];
  withIndex.forEach(k => {
    result[headers[k].index as number] = k;
  });
  withoutIndex.forEach(k => {
    let i = 0;
    while (result[i] !== undefined) i++;
    result[i] = k;
  });
  return result.filter(Boolean);
}
